import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, AlertCircle, Info, X, ExternalLink } from 'lucide-react';
import type { BudgetAlert } from '@/types/budget.types';

interface BudgetAlertsSectionProps {
  alerts: BudgetAlert[];
  onDismiss?: (alertId: number) => void;
  onViewDetails?: (alertId: number) => void;
  onTakeAction?: (alertId: number) => void;
}

export function BudgetAlertsSection({
  alerts,
  onDismiss,
  onViewDetails,
  onTakeAction,
}: BudgetAlertsSectionProps) {
  if (!alerts || alerts.length === 0) {
    return null;
  }

  // Group alerts by severity
  const criticalAlerts = alerts.filter((a) => a.severity === 'critical');
  const warningAlerts = alerts.filter((a) => a.severity === 'warning');
  const infoAlerts = alerts.filter((a) => a.severity === 'info');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Budget Alerts
            <Badge variant="secondary">{alerts.length}</Badge>
          </CardTitle>
          {alerts.length > 3 && (
            <Button variant="ghost" size="sm">
              View All
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Critical Alerts */}
        {criticalAlerts.map((alert) => (
          <BudgetAlertCard
            key={alert.id}
            alert={alert}
            onDismiss={onDismiss}
            onViewDetails={onViewDetails}
            onTakeAction={onTakeAction}
          />
        ))}

        {/* Warning Alerts */}
        {warningAlerts.map((alert) => (
          <BudgetAlertCard
            key={alert.id}
            alert={alert}
            onDismiss={onDismiss}
            onViewDetails={onViewDetails}
            onTakeAction={onTakeAction}
          />
        ))}

        {/* Info Alerts */}
        {infoAlerts.map((alert) => (
          <BudgetAlertCard
            key={alert.id}
            alert={alert}
            onDismiss={onDismiss}
            onViewDetails={onViewDetails}
            onTakeAction={onTakeAction}
          />
        ))}
      </CardContent>
    </Card>
  );
}

interface BudgetAlertCardProps {
  alert: BudgetAlert;
  onDismiss?: (alertId: number) => void;
  onViewDetails?: (alertId: number) => void;
  onTakeAction?: (alertId: number) => void;
}

function BudgetAlertCard({
  alert,
  onDismiss,
  onViewDetails,
  onTakeAction,
}: BudgetAlertCardProps) {
  const getAlertIcon = (severity: BudgetAlert['severity']) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="h-5 w-5 text-error-600 dark:text-error-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-warning-600 dark:text-warning-500" />;
      case 'info':
        return <Info className="h-5 w-5 text-info-600 dark:text-info-500" />;
    }
  };

  const getAlertBgColor = (severity: BudgetAlert['severity']) => {
    switch (severity) {
      case 'critical':
        return 'bg-error-50 dark:bg-error-950/20 border-error-200 dark:border-error-800';
      case 'warning':
        return 'bg-warning-50 dark:bg-warning-950/20 border-warning-200 dark:border-warning-800';
      case 'info':
        return 'bg-info-50 dark:bg-info-950/20 border-info-200 dark:border-info-800';
    }
  };

  const getAlertTextColor = (severity: BudgetAlert['severity']) => {
    switch (severity) {
      case 'critical':
        return 'text-error-900 dark:text-error-100';
      case 'warning':
        return 'text-warning-900 dark:text-warning-100';
      case 'info':
        return 'text-info-900 dark:text-info-100';
    }
  };

  const getSeverityBadge = (severity: BudgetAlert['severity']) => {
    switch (severity) {
      case 'critical':
        return (
          <Badge variant="destructive" className="uppercase text-xs">
            Critical
          </Badge>
        );
      case 'warning':
        return (
          <Badge className="bg-warning-500 hover:bg-warning-600 uppercase text-xs">
            Warning
          </Badge>
        );
      case 'info':
        return (
          <Badge variant="secondary" className="uppercase text-xs">
            Info
          </Badge>
        );
    }
  };

  return (
    <div className={`p-4 rounded-lg border ${getAlertBgColor(alert.severity)}`}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">{getAlertIcon(alert.severity)}</div>

        {/* Content */}
        <div className="flex-1 space-y-2">
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                {getSeverityBadge(alert.severity)}
                <span className={`font-medium ${getAlertTextColor(alert.severity)}`}>
                  {alert.title}
                </span>
              </div>
              {alert.category && (
                <div className="text-xs text-muted-foreground">
                  Category: {alert.category}
                </div>
              )}
            </div>
            {onDismiss && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0"
                onClick={() => onDismiss(alert.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Message */}
          <p className={`text-sm ${getAlertTextColor(alert.severity)}`}>
            {alert.message}
          </p>

          {/* Details */}
          {alert.details && (
            <p className="text-xs text-muted-foreground">{alert.details}</p>
          )}

          {/* Actions */}
          {(onViewDetails || onTakeAction || alert.actionLabel) && (
            <div className="flex items-center gap-2 pt-2">
              {onViewDetails && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewDetails(alert.id)}
                >
                  View Details
                </Button>
              )}
              {onTakeAction && alert.actionLabel && (
                <Button size="sm" onClick={() => onTakeAction(alert.id)}>
                  {alert.actionLabel}
                </Button>
              )}
            </div>
          )}

          {/* Timestamp */}
          {alert.timestamp && (
            <div className="text-xs text-muted-foreground pt-1">
              {new Date(alert.timestamp).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
